import { useState } from "react";
import { Table } from "../../../../../../components/Functional";
import MultiSelect from "../../../../../../components/ui/Input/components/MultiSelect";
import useOfferStatus from "../../../../../../hooks/useOfferStatus";
import { formatSalesPeopleNames } from "../../../../../../components/Functional/SalesPeopleSplitEditor/salesPeopleSplitEditorUtils";
import { FETCH_OFFERS_V2 as fetchOffersV2 } from "../../../../../../graphql/query/Offer";
import OfferContactCell from "../../../../../Offer/components/OfferContactCell";
import SectionHeader from "../../components/SectionHeader";

// Statuses the offer model counts when it puts an instance on hold; only these
// holds have a place in the hold order.
const ACTIVE_HOLD_STATUS_IDS = [1, 5]; // Pending, To Be Invoiced

const getHoldsVariables = (instanceId, statusIds = [1]) => ({
  holdId: { operator: "eq", value: +instanceId },
  ...(statusIds.length && {
    statusId: { operator: "in", value: statusIds },
  }),
  order: [["holdDate", "ASC"]],
});

const getHoldOrder = offer =>
  ACTIVE_HOLD_STATUS_IDS.includes(offer?.statusId) ? offer?.rank : null;

const InstanceHolds = props => {
  const { artworkId, instanceId } = props || {};
  const { offerStatuses, loading } = useOfferStatus();
  const [selectedStatuses, setSelectedStatuses] = useState([
    { label: "Pending", value: 1 },
  ]);
  const tableProps = {
    variant: "dynamicHeight",
    dataKey: "offersV2",
    headers: [
      {
        label: "Hold Order",
        name: "rank",
        type: "custom",
        component: ({ values }) => getHoldOrder(values),
      },
      { label: "Hold Date", name: "holdDate", type: "date" },
      {
        label: "Contact",
        name: "contact.name",
        type: "custom",
        component: ({ values }) => (
          <OfferContactCell values={values} artworkId={artworkId} />
        ),
      },
      { label: "Status", name: "status.description", type: "label" },
      {
        label: "Selling People",
        name: "offerSalesPeople",
        type: "custom",
        component: ({ values }) =>
          formatSalesPeopleNames(values?.offerSalesPeople),
      },
      { label: "Offer Amount", name: "amount", type: "currency" },
      { label: "Currency", name: "currency.code", type: "label" },
      { label: "Offer Date", name: "date", type: "date" },
      { label: "Notes", name: "notes", type: "label" },
    ],
    FETCH_QUERY: fetchOffersV2({
      contact: { name: true, id: true },
      contactId: true,
      currency: {},
      currencyId: true,
      status: {},
      statusId: true,
      hold: { name: true, id: true },
      holdId: true,
      rank: true,
      amount: true,
      tbiAmount: true,
      tbiDate: true,
      offerSalesPeople: true,
      date: true,
      holdDate: true,
      dateClosed: true,
      priority: true,
      notes: true,
      projectCode: true,
    }),
    variables: getHoldsVariables(
      instanceId,
      selectedStatuses.map(status => status.value),
    ),
    skip: () => !instanceId,
    hideSinglePagePagination: true,
    showRefresh: false,
  };

  return (
    <div className="w-full">
      <SectionHeader
        title="Holds"
        actions={[
          {
            component: () => (
              <div className="w-72 max-w-full font-normal">
                <MultiSelect
                  name="holdStatuses"
                  inputId="holdStatuses"
                  aria-label="Status"
                  isMulti
                  isClearable
                  isLoading={loading}
                  placeholder="All statuses"
                  options={offerStatuses.map(status => ({
                    label: status.description,
                    value: Number(status.id),
                  }))}
                  selected={selectedStatuses}
                  onChange={statuses => setSelectedStatuses(statuses || [])}
                />
              </div>
            ),
          },
        ]}
      />
      <div className="mb-6 w-full">
        <Table {...tableProps} />
      </div>
    </div>
  );
};

export { getHoldOrder, getHoldsVariables };
export default InstanceHolds;
